/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import Edit from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  Grid,
  Paper,
  Popper,
  Stack,
  type Theme,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import type { BaseTextFieldProps } from './BaseTextField';
import FormArrayTextField from './FormArrayTextField';
import { useFieldContext } from './formContext';

const paperSx = (theme: Theme) => ({
  bgcolor: theme.palette.background.default,
  overflowY: 'hidden',
  zIndex: 5,
  borderRadius: 2,
  p: 2,
  mr: 1,
  width: 250,
});

interface Props extends Pick<BaseTextFieldProps, 'numberFormat'> {
  label?: React.ReactNode;
  type?: React.HTMLInputTypeAttribute;
  customButtonLabel?: React.ReactNode;
  disabled?: boolean;
}

function ArrayTextFieldsPopper({
  label,
  type,
  customButtonLabel,
  numberFormat,
  disabled,
}: Readonly<Props>) {
  const field = useFieldContext<Array<string>>();
  const terminalName = field.name.split('.').at(-1);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const handleButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  }, []);

  const handleClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  return useMemo(
    () => (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box>
          <Button
            sx={{ borderRadius: 8 }}
            onClick={handleButtonClick}
            variant={open ? 'contained' : 'outlined'}
            startIcon={<Edit />}
            color="primary"
            disabled={disabled}
            data-testid={`popper-${terminalName}`}
          >
            <Typography
              sx={{
                fontWeight: 'fontWeightBold',
                fontSize: 14,
              }}
            >
              {customButtonLabel ?? label}
            </Typography>
          </Button>

          <Popper
            id={open && Boolean(anchorEl) ? 'transition-popper' : undefined}
            open={open}
            anchorEl={anchorEl}
            transition
            placement="left"
            onClick={(e) => e.stopPropagation()}
            sx={{ zIndex: 2000 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper elevation={3} sx={paperSx}>
                  <Stack>
                    <Typography variant="h6">Edit {label}</Typography>
                    <Box
                      sx={{
                        maxHeight: 500,
                        pr: 1,
                        overflowY: 'auto',
                      }}
                    >
                      <Grid
                        container
                        spacing={2}
                        sx={{
                          pt: 2,
                        }}
                      >
                        <Grid size="grow">
                          <FormArrayTextField type={type} numberFormat={numberFormat} />
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      </ClickAwayListener>
    ),
    [
      anchorEl,
      customButtonLabel,
      disabled,
      handleButtonClick,
      handleClickAway,
      label,
      open,
      type,
      numberFormat,
      terminalName,
    ],
  );
}

export default ArrayTextFieldsPopper;
